import React from 'react';
import { Box, Grid, IconButton } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { GridFilterModel, GridColDef } from '@mui/x-data-grid';
import SelectInput from '../SelectInput';
import ValueInput from './ValueInput';
import { getDefaultOperators } from './filter-panel.util';

interface Props {
  columns: GridColDef[];
  filterModel: GridFilterModel;
  setFilterModel: (newVal: GridFilterModel) => void;
  skippedFields?: string[];
}

const FilterPanel: React.FC<Props> = ({ columns, filterModel, setFilterModel, skippedFields = [] }) => {
  if (columns.length == 0) return <b>no columns provided</b>;

  columns = columns
    .filter(column => column.filterable == undefined || column.filterable == true)
    .filter(column => !skippedFields.includes(column.field))
    .map(column => {
      column.type = column.type ?? 'string';
      column.filterOperators = column.filterOperators ?? getDefaultOperators(column.type);
      return column;
    });

  React.useEffect(() => {
    if (filterModel.items.filter(item => !skippedFields.includes(item.field)).length == 0) addFilter();
  }, []);

  const addFilter = () => {
    const newFilterItems = [
      ...filterModel.items,
      {
        id: Date.now().toString(),
        field: columns[0].field,
        operator: columns[0].filterOperators ? columns[0].filterOperators[0].value : '',
        value: '',
      },
    ];
    setFilterModel({ ...filterModel, items: newFilterItems });
  };

  const removeFilter = (id: string) => {
    const newFilterItems = filterModel.items.filter(item => item.id !== id);
    setFilterModel({ ...filterModel, items: newFilterItems });
  };

  const changeFilterProperty = (id: string, property: string, value: string | number | Date | boolean | null) => {
    const newFilterItems = filterModel.items.map(item => {
      if (item.id === id) {
        let newItem = { ...item, [property]: value };
        if (property === 'field') {
          const targetColumnOperators = columns.find(c => c.field === value)?.filterOperators ?? [];
          newItem = { ...newItem, operator: targetColumnOperators[0]?.value, value: '' };
        }
        return newItem;
      }
      return item;
    });
    setFilterModel({ ...filterModel, items: newFilterItems });
  };

  return (
    <Box p={2}>
      <Grid container spacing={2} sx={{ width: '500px' }}>
        {filterModel.items
          .filter(item => !skippedFields.includes(item.field))
          .map((item, index) => {
            const field = columns.find(c => c.field == item.field);
            const fieldType = field?.type ?? 'string';
            const operators = field?.filterOperators?.map(op => op.value) ?? [];
            return (
              <Grid container key={index} spacing={1} alignItems={'center'}>
                <Grid size={{ xs: 12 }}>
                  <SelectInput
                    label='Columns'
                    selected={item.field}
                    setSelected={newVal => {
                      changeFilterProperty(item.id as string, 'field', newVal);
                    }}
                    list={columns.map(c => {
                      return { label: c.headerName ?? c.field, value: c.field };
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <SelectInput
                    label='Operator'
                    selected={item.operator}
                    setSelected={newVal => {
                      changeFilterProperty(item.id as string, 'operator', newVal);
                    }}
                    list={operators.map(o => {
                      return { label: o, value: o };
                    })}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <ValueInput
                    label='Value'
                    type={fieldType}
                    value={item.value}
                    setValue={newVal => {
                      changeFilterProperty(item.id as string, 'value', newVal);
                    }}
                    variant='standard'
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 1 }}>
                  <IconButton onClick={() => removeFilter(item.id as string)} title='delete this filter'>
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}
        <Grid container>
          <Grid size={{ xs: 1 }} sx={{ margin: 'auto' }}>
            <IconButton onClick={addFilter} color='primary' title='add new filter'>
              <Add />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
export default FilterPanel;
